import { useLocation } from '@reach/router';
import {
  LessonsAccess,
  LessonsApi,
  LessonsQuery,
  LessonsResult,
  Subject,
  SubjectContents,
} from 'api/lessonsApi';
import { AppPath } from 'appConstants';
import queryString from 'query-string';
import { useEffect, useState } from 'react';

export type LessonsRouteParams = {
  subject?: string;
  keyStage?: string;
  activity?: string;
  lessonNumber?: string;
};

export type LessonsPageData<T> = {
  subject: Subject;
  labels: SubjectContents;
  query: LessonsQuery;
  details: T;
};

const YEARS_PATTERN = /^\d+(,\d+)*$/;
const LESSON_NUMBER_PATTERN = /^\d{1,9}$/;

export const unitPath = (
  subjectCode: string,
  keyStage: string,
  activity: string
) =>
  `/${subjectCode.toLowerCase()}/lesson/${keyStage.toLowerCase()}/${activity.toLowerCase()}`;

export const splitLines = (text: string | null) =>
  (text ?? '').split(/\r?\n/).filter((line) => line.trim() !== '');

/**
 * Loads a lesson page's subject, labels and details from its path and ?years=.
 * A bad path, an unknown subject or a refused call is 'notFound'.
 */
export const useLessonsPage = <T>(
  params: LessonsRouteParams,
  load: (
    query: LessonsQuery,
    lessonNumber: string
  ) => Promise<LessonsResult<T>>
): LessonsResult<LessonsPageData<T>> | null => {
  const { search } = useLocation();
  const [result, setResult] = useState<LessonsResult<
    LessonsPageData<T>
  > | null>(null);
  const yearsParam = queryString.parse(search).years;
  const years = typeof yearsParam === 'string' ? yearsParam : '';
  const { subject: code, keyStage, activity, lessonNumber } = params;

  useEffect(() => {
    let bCurrent = true;
    const done = (value: LessonsResult<LessonsPageData<T>>) => {
      if (bCurrent) setResult(value);
    };

    setResult(null);
    if (
      !code ||
      !keyStage ||
      !activity ||
      !YEARS_PATTERN.test(years) ||
      (lessonNumber !== undefined && !LESSON_NUMBER_PATTERN.test(lessonNumber))
    ) {
      done('notFound');
      return;
    }

    (async () => {
      const subject = await LessonsApi.getSubjectByCode(code);
      if (typeof subject === 'string') return done(subject);

      const query = { subjectsId: subject.subjectsID, keyStage, activity, years };
      const [details, labels] = await Promise.all([
        load(query, lessonNumber ?? ''),
        LessonsApi.getSubjectContents(subject.subjectsID),
      ]);
      if (typeof details === 'string') return done(details);

      done({ subject, labels, query, details });
    })();

    return () => {
      bCurrent = false;
    };
    // load is a fixed api call per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, keyStage, activity, lessonNumber, years]);

  return result;
};

export type AccessButton = {
  text: string;
  href: string | null;
  bDisabled: boolean;
};

/**
 * The button for an item the school user cannot open, by why they cannot
 */
export const closedAccessButton = (access: LessonsAccess): AccessButton => {
  switch (access.status) {
    case 'AccountPaused':
      return { text: 'account paused', href: null, bDisabled: true };
    case 'CompleteSubscriptionPayment':
      return {
        text: 'Complete Subscription Payment',
        href: AppPath.UPGRADE_SUBSCRIPTION,
        bDisabled: false,
      };
    case 'UpgradePackage':
      return { text: 'Upgrade Package', href: AppPath.DASHBOARD, bDisabled: false };
    default:
      return { text: 'Subscribers Only', href: null, bDisabled: true };
  }
};

/**
 * Opens a file link in a new tab. The tab opens before the link is fetched, as one opened after
 * an api call is blocked as a popup.
 * @returns false when the link could not be fetched
 */
export const openFileInNewTab = async (
  getUrl: () => Promise<string | null>,
  fileWindow: Window | null = window.open('', '_blank')
) => {
  const url = await getUrl();
  if (url && fileWindow) {
    fileWindow.location.href = url;
    return true;
  }
  fileWindow?.close();
  return false;
};

/**
 * Downloads a file link in place: the link is served as an attachment, so following it from a
 * hidden anchor saves the file without leaving the page or opening a tab.
 * @returns false when the link could not be fetched
 */
export const downloadFileInPlace = async (getUrl: () => Promise<string | null>) => {
  const url = await getUrl();
  if (!url) {
    return false;
  }
  const link = document.createElement('a');
  link.href = url;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
};
